import { useRef, useEffect, useState, useCallback } from 'react';
import { Trash2, Undo2 } from 'lucide-react';

export interface StrokePoint {
  x: number;
  y: number;
  pressure?: number;
  timestamp: number;
}

export interface Stroke {
  points: StrokePoint[];
  color: string;
  width: number;
}

interface SignaturePadProps {
  onChange: (payload: { strokes: Stroke[]; dataUrl: string } | null) => void;
  height?: number;
  penColor?: string;
  penWidth?: number;
}

/**
 * Canvas-based signature pad supporting mouse, touch and pen input.
 * Emits stroke vectors (for server-side PDF drawing) plus a PNG data URL (for preview).
 */
export default function SignaturePad({
  onChange,
  height = 180,
  penColor = '#1e3a8a',
  penWidth = 2.2,
}: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [strokes, setStrokes] = useState<Stroke[]>([]);
  const currentStroke = useRef<Stroke | null>(null);
  const drawing = useRef(false);

  const getContext = () => canvasRef.current?.getContext('2d') ?? null;

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const dpr = window.devicePixelRatio || 1;
    const width = container.clientWidth;

    // Preserve existing drawing
    const previous = canvas.toDataURL();

    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const ctx = getContext();
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Restore
    const img = new Image();
    img.onload = () => ctx.drawImage(img, 0, 0, width, height);
    img.src = previous;
  }, [height]);

  useEffect(() => {
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, [resizeCanvas]);

  // Seed from existing strokes (e.g. server draft)
  useEffect(() => {
    if (strokes.length === 0) return;
    redraw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPoint = (e: React.PointerEvent<HTMLCanvasElement>): StrokePoint => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      pressure: e.pressure || 0.5,
      timestamp: Date.now(),
    };
  };

  const startStroke = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    canvasRef.current?.setPointerCapture(e.pointerId);
    drawing.current = true;

    const ctx = getContext();
    if (!ctx) return;

    const point = getPoint(e);
    currentStroke.current = {
      points: [point],
      color: penColor,
      width: penWidth * (point.pressure && point.pressure > 0 ? 0.6 + point.pressure : 1),
    };

    ctx.beginPath();
    ctx.strokeStyle = penColor;
    ctx.lineWidth = currentStroke.current.width;
    ctx.moveTo(point.x, point.y);
  };

  const moveStroke = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current || !currentStroke.current) return;
    e.preventDefault();

    const ctx = getContext();
    if (!ctx) return;

    const point = getPoint(e);
    const points = currentStroke.current.points;
    const prev = points[points.length - 1];
    points.push(point);

    // Smooth via quadratic midpoints
    const midX = (prev.x + point.x) / 2;
    const midY = (prev.y + point.y) / 2;
    ctx.quadraticCurveTo(prev.x, prev.y, midX, midY);
    ctx.stroke();
  };

  const endStroke = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    e.preventDefault();
    drawing.current = false;

    if (currentStroke.current && currentStroke.current.points.length > 0) {
      const next = [...strokes, currentStroke.current];
      setStrokes(next);
      emit(next);
    }
    currentStroke.current = null;
  };

  const emit = (next: Stroke[]) => {
    if (next.length === 0) {
      onChange(null);
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) return;
    onChange({ strokes: next, dataUrl: canvas.toDataURL('image/png') });
  };

  const redraw = () => {
    const canvas = canvasRef.current;
    const ctx = getContext();
    if (!canvas || !ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const width = canvas.width / dpr;
    ctx.clearRect(0, 0, width, height);

    for (const stroke of strokes) {
      if (stroke.points.length === 0) continue;
      ctx.beginPath();
      ctx.strokeStyle = stroke.color;
      ctx.lineWidth = stroke.width;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.moveTo(stroke.points[0].x, stroke.points[0].y);
      for (let i = 1; i < stroke.points.length; i++) {
        const prev = stroke.points[i - 1];
        const cur = stroke.points[i];
        ctx.quadraticCurveTo(prev.x, prev.y, (prev.x + cur.x) / 2, (prev.y + cur.y) / 2);
      }
      ctx.stroke();
    }
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = getContext();
    if (!canvas || !ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.clearRect(0, 0, canvas.width / dpr, height);
    setStrokes([]);
    onChange(null);
  };

  const undo = () => {
    if (strokes.length === 0) return;
    const next = strokes.slice(0, -1);
    setStrokes(next);
    redraw();
    emit(next);
  };

  return (
    <div>
      <div ref={containerRef} className="relative">
        <canvas
          ref={canvasRef}
          className="signature-pad w-full cursor-crosshair touch-none block"
          style={{ height }}
          onPointerDown={startStroke}
          onPointerMove={moveStroke}
          onPointerUp={endStroke}
          onPointerLeave={endStroke}
          onPointerCancel={endStroke}
        />
        {strokes.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-sm text-gray-400">Ký tại đây bằng chuột hoặc cảm ứng</p>
          </div>
        )}
        <div className="absolute bottom-2 right-2 flex gap-1">
          <button
            type="button"
            onClick={undo}
            disabled={strokes.length === 0}
            className="p-1.5 bg-white/90 border border-gray-200 rounded-md text-gray-600 hover:bg-gray-50 disabled:opacity-40"
            title="Hoàn tác"
          >
            <Undo2 className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={clear}
            disabled={strokes.length === 0}
            className="p-1.5 bg-white/90 border border-gray-200 rounded-md text-danger-600 hover:bg-danger-50 disabled:opacity-40"
            title="Xóa"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      <p className="text-xs text-gray-500 mt-2">
        {strokes.length > 0
          ? `Đã vẽ ${strokes.length} nét`
          : 'Chữ ký sẽ được gắn vào tài liệu PDF khi bạn nhấn "Ký tài liệu".'}
      </p>
    </div>
  );
}